import { FC, useCallback, useMemo } from 'react';
import { Field } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import ThemedFormSelect from 'inquiry/components/InputSelect/ThemedFormSelect';
import ThemedMaskedInput from 'inquiry/components/ThemedMaskedInput';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import { parseEmptyStringToNull } from 'inquiry/lib/form';
import { labelSSTStyles } from 'inquiry/mixins/form';
import {
  InputInternationalDbComponent,
  InputSelectComponent,
  InternationalId,
} from 'lib/interfaces/ui-components';
import { Rule } from 'lib/utils/formValidation';

export interface CountryOption {
  name: string;
  code: string;
}

export interface IdTypeOption {
  idbType: string;
  idbName: string;
}

interface ThemedFormInternationalDbProps {
  allowedIdTypes: InternationalId[];
  countryOptions: CountryOption[];
  idTypeOptions: IdTypeOption[];
  hideCountryIfPrefilled: boolean;
  hideTypeIfPrefilled: boolean;
  hideCountryIfSingleChoice: boolean;
  hideTypeIfSingleChoice: boolean;
  // field keys
  fieldKeyIdbCountry: string;
  fieldKeyIdbType: string;
  fieldKeyIdbValue: string;
  // Translations
  label: string;
  labelIdbCountry: string;
  labelIdbType: string;
  labelIdbValue: string;
  placeholderIdbCountry: string;
  placeholderIdbType: string;
  placeholderIdbValue: string;
  // validation rules
  validateIdbCountry: Rule[];
  validateIdbType: Rule[];
  validateIdbValue: Rule[];
  // server errors
  serverErrorIdbCountry?: string;
  serverErrorIdbType?: string;
  serverErrorIdbValue?: string;
  // Other
  styles?: InputInternationalDbComponent['styles'];
  disabled?: boolean;
  setField: (field: string, value: string) => void;
  selectedCountry?: string;
  selectedType?: string;
  countryPrefilled: boolean;
  typePrefilled: boolean;
}

const OutlinedLabelContainer = styled('div')<{
  styles?: InputSelectComponent['styles'];
}>`
  ${({ styles }) => labelSSTStyles(styles)}
`;

const ThemedFormInternationalDb: FC<ThemedFormInternationalDbProps> = ({
  allowedIdTypes,
  countryOptions,
  idTypeOptions,
  hideCountryIfPrefilled,
  hideTypeIfPrefilled,
  hideCountryIfSingleChoice,
  hideTypeIfSingleChoice,
  // field keys
  fieldKeyIdbCountry,
  fieldKeyIdbType,
  fieldKeyIdbValue,
  // translations
  label,
  labelIdbCountry,
  labelIdbType,
  labelIdbValue,
  placeholderIdbCountry,
  placeholderIdbType,
  placeholderIdbValue,
  // validation rules
  validateIdbCountry,
  validateIdbType,
  validateIdbValue,
  // server errors
  serverErrorIdbCountry,
  serverErrorIdbType,
  serverErrorIdbValue,

  // Other
  disabled,
  styles,
  setField,
  selectedCountry,
  selectedType,
  countryPrefilled,
  typePrefilled,
}: ThemedFormInternationalDbProps) => {
  const valueProps: {
    mask: false | (string | RegExp)[];
    placeholder: string | undefined;
    secure: boolean;
    description: string | undefined;
  } = useMemo(() => {
    const idType = allowedIdTypes.find(
      (idb: InternationalId) =>
        idb['country-code'] === selectedCountry && idb['id-type'] === selectedType,
    );

    const placeholder = idType?.placeholder;
    const secure = !!idType?.secure;
    const description = idType?.description;
    const mask =
      idType?.mask != null && idType.mask !== ''
        ? idType.mask.split('').map((char: string) => {
            switch (char) {
              case '#': // number
                return /\d/;
              case '@': // all letters
                return /[a-z]/i;
              case '*': // number or letter
                return /\w/;
              default:
                return char;
            }
          })
        : false;

    return { description, mask, placeholder, secure };
  }, [allowedIdTypes, selectedCountry, selectedType]);

  const hideCountry = useMemo(() => {
    return (
      (countryPrefilled && hideCountryIfPrefilled) ||
      (countryOptions.length === 1 && hideCountryIfSingleChoice)
    );
  }, [countryOptions, countryPrefilled, hideCountryIfPrefilled, hideCountryIfSingleChoice]);

  const hideType = useMemo(() => {
    return (
      (typePrefilled && hideTypeIfPrefilled) ||
      (idTypeOptions.length === 1 && hideTypeIfSingleChoice)
    );
  }, [idTypeOptions, typePrefilled, hideTypeIfPrefilled, hideTypeIfSingleChoice]);

  // If country changes, ID types must change and value reset
  const handleCountryChange = useCallback(() => {
    setField(fieldKeyIdbValue, '');
    setField(fieldKeyIdbType, '');
  }, [fieldKeyIdbType, fieldKeyIdbValue, setField]);

  // If type changes, value must reset
  // TODO: [International DB] this doesn't work when switching to an id type with mask=false
  const handleTypeChange = useCallback(() => {
    setField(fieldKeyIdbValue, '');
  }, [fieldKeyIdbValue, setField]);

  return (
    <>
      {label && (
        <OutlinedLabelContainer styles={styles?.['input-select-style']?.base?.base}>
          <Markdown<'label'>
            wrapper='label'
            wrapperProps={{
              // remove gap between this and sublabels
              style: { marginBottom: 0 },
            }}
          >
            {label}
          </Markdown>
        </OutlinedLabelContainer>
      )}
      {!hideCountry && (
        <Field
          data-test='idb-country'
          id={fieldKeyIdbCountry}
          name={fieldKeyIdbCountry}
          component={ThemedFormSelect}
          disabled={disabled}
          validate={validateIdbCountry}
          label={labelIdbCountry}
          styles={styles?.['input-select-style']?.base?.base}
          serverError={serverErrorIdbCountry}
          onChange={handleCountryChange}
        >
          <option value='' disabled>
            {placeholderIdbCountry ?? ''}
          </option>
          {countryOptions.map((option: { name: string; code: string }) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </Field>
      )}
      {!hideType && (
        <Field
          data-test='idb-type'
          id={fieldKeyIdbType}
          name={fieldKeyIdbType}
          component={ThemedFormSelect}
          disabled={disabled || !selectedCountry}
          validate={validateIdbType}
          label={labelIdbType}
          styles={styles?.['input-select-style']?.base?.base}
          serverError={serverErrorIdbType}
          onChange={handleTypeChange}
        >
          <option value='' disabled>
            {placeholderIdbType ?? ''}
          </option>
          {idTypeOptions.map((option: { idbName: string; idbType: string }) => (
            <option key={option.idbType} value={option.idbType}>
              {option.idbName}
            </option>
          ))}
        </Field>
      )}
      <Field
        data-test='idb-value'
        name={fieldKeyIdbValue}
        id={fieldKeyIdbValue}
        component={ThemedMaskedInput}
        disabled={disabled || !selectedCountry || !selectedType}
        parse={parseEmptyStringToNull}
        validate={validateIdbValue}
        label={labelIdbValue}
        mask={valueProps?.mask}
        placeholder={valueProps?.placeholder || placeholderIdbValue}
        secure={valueProps?.secure}
        styles={styles?.['input-text-style']?.base?.base}
        serverError={serverErrorIdbValue}
      />
      {!!valueProps?.description && (
        <ThemedParagraph styles={styles?.['text-style']?.base?.base}>
          {valueProps?.description}
        </ThemedParagraph>
      )}
    </>
  );
};

export default ThemedFormInternationalDb;
